import { Outlet, NavLink, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { notificationsAPI, default as API } from '../lib/api';
import { LayoutDashboard, Wallet, ArrowLeftRight, Clock, Shield, Bell, LogOut, Menu, X, ChevronDown, ChevronUp, Zap, User, Users, Repeat, Key, Send, ArrowDownLeft, Settings, ShieldCheck, ShieldAlert, Fingerprint, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import { timeAgo } from '../lib/utils';
import NotificationPopup, { NotifTypeIcon } from './NotificationPopup';
import AnimatedBackground from './AnimatedBackground';
import MobileBottomNav from './MobileBottomNav';
import { useNotificationWS } from '../hooks/useNotificationWS';
import '../App.css';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Панель' },
  { path: '/wallets', icon: Wallet, label: 'Кошельки' },
  { path: '/send', icon: Send, label: 'Отправить' },
  { path: '/receive', icon: ArrowDownLeft, label: 'Получить' },
  { path: '/exchange', icon: ArrowLeftRight, label: 'Обмен' },
  { path: '/transactions', icon: Clock, label: 'История' },
];

const TELEGRAM_SUPPORT_URL = 'https://t.me/FintechPlatformNetWork';

const adminNavSections = [
  { title: 'ОБЗОР', items: [{ path: '/admin', icon: LayoutDashboard, label: 'Панель' }] },
  { title: 'КЛИЕНТЫ', items: [{ path: '/admin/users', icon: Users, label: 'Пользователи' }] },
  { title: 'ФИНАНСЫ', items: [
    { path: '/admin/transactions', icon: Repeat, label: 'Транзакции' },
    { path: '/admin/wallets', icon: Wallet, label: 'Кошельки' },
  ]},
  { title: 'ОПЕРАЦИИ', items: [
    { path: '/admin/notifications', icon: Bell, label: 'Уведомления' },
    { path: '/admin/password', icon: Key, label: 'Смена пароля' },
    { path: '/admin/status', icon: Settings, label: 'Статус системы' },
  ]},
];

function SidebarLink({ to, icon: Icon, label, end, onClick, locked }) {
  return (
    <NavLink to={to} end={end} onClick={onClick} data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
      className={({ isActive }) => `nav-pill nav-icon-bounce group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all duration-200 ${isActive ? 'nav-active text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-white/[0.06] hover:shadow-sm'}`}>
      {({ isActive }) => (
        <>
          {isActive && (
            <motion.div layoutId="nav-glow" className="absolute inset-0 rounded-xl bg-primary/[0.08] border border-primary/20" transition={{ type: 'spring', stiffness: 350, damping: 30 }} />
          )}
          {isActive && (
            <motion.div layoutId="nav-bar" className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full" style={{ background: '#FF003C', boxShadow: '0 0 10px rgba(255,0,60,0.6), 0 0 20px rgba(255,0,60,0.25)' }} transition={{ type: 'spring', stiffness: 350, damping: 30 }} />
          )}
          <span className="relative z-10 flex items-center gap-3 w-full">
            <span className="nav-icon-inner inline-flex">
              <Icon size={17} strokeWidth={isActive ? 2 : 1.5} className={`transition-all duration-200 ${isActive ? 'text-primary drop-shadow-[0_0_6px_hsl(var(--primary)/0.4)]' : ''}`} />
            </span>
            <span className="flex-1">{label}</span>
            {locked && <Shield size={13} className="text-amber-500/60" />}
          </span>
        </>
      )}
    </NavLink>
  );
}

function VerifBanner({ status, to, onClick }) {
  const color = status === 'verified' ? 'emerald' : status === 'pending' ? 'amber' : 'rose';
  const text = status === 'verified' ? 'Пройдена' : status === 'pending' ? 'На проверке' : 'Требуется';
  const Icon = status === 'verified' ? ShieldCheck : status === 'pending' ? Fingerprint : ShieldAlert;
  return (
    <NavLink to={to} onClick={onClick} data-testid="nav-verification"
      className={`flex items-center justify-between px-3 py-3 rounded-xl border transition-all duration-300 hover:scale-[1.01] ${
        status === 'verified' ? 'bg-emerald-500/[0.06] border-emerald-500/20 hover:border-emerald-500/40' :
        status === 'pending' ? 'bg-amber-500/[0.06] border-amber-500/20 hover:border-amber-500/40' :
        'bg-rose-500/[0.06] border-rose-500/20 hover:border-rose-500/40'
      }`}>
      <div className="flex items-center gap-2.5">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
          status === 'verified' ? 'bg-emerald-500/15' : status === 'pending' ? 'bg-amber-500/15' : 'bg-rose-500/15'
        }`}>
          <Icon size={15} className={`text-${color}-500`} />
        </div>
        <span className="text-[12px] font-bold tracking-wide">Верификация</span>
      </div>
      <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${
        status === 'verified' ? 'bg-emerald-500/15 text-emerald-400' :
        status === 'pending' ? 'bg-amber-500/15 text-amber-400' :
        'bg-rose-500/15 text-rose-400'
      }`}>{text}</span>
    </NavLink>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [rates, setRates] = useState({});
  const [expandedNotif, setExpandedNotif] = useState(null);

  // Real-time notification toasts via WebSocket
  const token = typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem('token') || localStorage.getItem('token')) : null;
  useNotificationWS(token);

  const fetchNotifications = useCallback(async () => {
    try { const { data } = await notificationsAPI.list(); setNotifications(data.notifications || []); setUnreadCount(data.unread_count || 0); } catch {}
  }, []);

  useEffect(() => { fetchNotifications(); const i = setInterval(fetchNotifications, 60000); return () => clearInterval(i); }, [fetchNotifications]);
  useEffect(() => {
    const fetchRates = async () => { try { const { data } = await API.get('/exchange/rates'); setRates(data.rates || {}); } catch {} };
    fetchRates(); const i = setInterval(fetchRates, 120000); return () => clearInterval(i);
  }, []);
  const markRead = async (id) => { try { await notificationsAPI.markRead(id); fetchNotifications(); } catch {} };
  const markAllRead = async () => { try { await notificationsAPI.markAllRead(); fetchNotifications(); } catch {} };

  const isAdmin = user?.is_admin;
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isVerificationRoute = location.pathname === '/verification';
  const needsVerification = !isAdmin && user?.verification_status === 'unverified';

  if (needsVerification && !isVerificationRoute) return <Navigate to="/verification" replace />;

  /* ═══ Minimal layout for unverified users ═══ */
  if (needsVerification && isVerificationRoute) {
    return (
      <div className="flex overflow-hidden bg-background relative noise-overlay" style={{ touchAction: 'manipulation', height: '100dvh', height: '100vh', maxHeight: '-webkit-fill-available' }}>
        <div className="app-bg" />
        <aside className="hidden md:flex flex-col w-64 border-r border-border/30 bg-card/80 glass z-10 relative">
          <div className="p-5 border-b border-border/30">
            <div className="flex items-center gap-3">
              <img src="/logo-icon.webp" alt="Fintech" width="36" height="36" className="w-9 h-9 object-contain" />
              <div><h1 className="text-[15px] font-bold tracking-tight"><span className="text-primary">Fintech</span> Platform</h1><p className="text-[10px] text-muted-foreground/70 -mt-0.5 tracking-wider">DIGITAL FINANCE</p></div>
            </div>
          </div>
          <nav className="flex-1 py-4 px-3 space-y-1">
            <div className="flex items-center gap-3 px-3 py-3 mb-4 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold ring-2 ring-primary/15 relative" style={{ background: 'linear-gradient(135deg, #00F0FF, #6B3FA0, #FF003C)' }}>
              {user?.username?.[0]?.toUpperCase()}
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-card live-dot" />
            </div>
              <div className="flex-1 min-w-0"><p className="text-[13px] font-semibold truncate">{user?.username}</p><p className="text-[10px] text-muted-foreground/70 truncate">{user?.email}</p></div>
            </div>
            <SidebarLink to="/verification" icon={Fingerprint} label="Верификация" />
          </nav>
          <div className="px-3 pb-4 pt-3 border-t border-border/30">
            <button onClick={logout} data-testid="nav-logout" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium text-muted-foreground hover:text-rose-400 hover:bg-rose-500/[0.06] transition-all w-full">
              <LogOut size={17} strokeWidth={1.5} />Выйти
            </button>
          </div>
        </aside>
        <main className="flex-1 overflow-y-auto relative z-10">
          <header className="sticky top-0 z-20 glass border-b border-border/30 px-6 h-14 flex items-center justify-end" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
          </header>
          <div className="p-6 md:p-8"><Outlet /></div>
        </main>
      </div>
    );
  }

  const showAdminSidebar = isAdmin && isAdminRoute;
  const allNavItems = isAdmin ? [...navItems, { path: '/admin', icon: Shield, label: 'Админ' }] : navItems;
  const vStatus = user?.verification_status || 'unverified';

  const renderNav = (onItemClick) => {
    if (showAdminSidebar) {
      return (
        <div className="space-y-5">
          {adminNavSections.map(section => (
            <div key={section.title}>
              <p className="px-3 text-[9px] font-bold uppercase tracking-[0.2em] text-muted-foreground/40 mb-2">{section.title}</p>
              <div className="space-y-0.5">
                {section.items.map(({ path, icon, label }) => (
                  <SidebarLink key={path} to={path} icon={icon} label={label} end={path === '/admin'} onClick={onItemClick} />
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="space-y-0.5">
        {allNavItems.map(({ path, icon, label }) => (
          <SidebarLink key={path} to={path} icon={icon} label={label} onClick={onItemClick} />
        ))}
      </div>
    );
  };

  const SidebarContent = ({ onItemClick, mobile }) => (
    <>
      <div className="p-5 border-b border-border/30">
        <div className="flex items-center gap-3">
          <img src="/logo-icon.webp" alt="Fintech" width="36" height="36" className="w-9 h-9 object-contain" />
          <div><h1 className="text-[15px] font-bold tracking-tight" data-testid="app-logo"><span className="text-primary">Fintech</span> Platform</h1><p className="text-[10px] text-muted-foreground/70 -mt-0.5 tracking-[0.15em]">DIGITAL FINANCE</p></div>
          {mobile && <Button variant="ghost" size="icon" className="ml-auto h-8 w-8" onClick={onItemClick} data-testid="close-mobile-menu"><X size={18} /></Button>}
        </div>
      </div>
      <nav className="flex-1 py-4 px-3 overflow-y-auto space-y-1">
        {!showAdminSidebar && (
          <NavLink to="/profile" onClick={onItemClick} data-testid="nav-profile"
            className={({ isActive }) => `flex items-center gap-3 px-3 py-3 mb-4 rounded-2xl transition-all duration-200 ${isActive ? 'border border-primary/15 ring-1 ring-primary/10' : 'border border-white/[0.06] hover:border-[rgba(0,240,255,0.15)]'}`}
            style={{ background: 'rgba(255,255,255,0.02)' }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold ring-2 ring-primary/15 relative" style={{ background: 'linear-gradient(135deg, #00F0FF, #6B3FA0, #FF003C)' }}>
              {user?.username?.[0]?.toUpperCase()}
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-card live-dot" />
            </div>
            <div className="flex-1 min-w-0"><p className="text-[13px] font-semibold truncate">{user?.username}</p><p className="text-[10px] text-muted-foreground/70 truncate">{user?.email}</p></div>
          </NavLink>
        )}
        {renderNav(onItemClick)}
      </nav>
      <div className="px-3 pb-4 space-y-2 border-t border-border/30 pt-4">
        {/* Mini market widget */}
        {!showAdminSidebar && (
          <div className="mb-3 px-2 py-2.5 rounded-xl" style={{ background: 'rgba(0,240,255,0.03)', border: '1px solid rgba(0,240,255,0.08)' }}>
            <p className="text-[8px] uppercase tracking-[0.15em] text-primary/50 font-semibold mb-2 px-1">Live Рынок</p>
            <div className="space-y-1.5">
              {[
                { sym: 'BTC', icon: '₿', color: '#F7931A' },
                { sym: 'ETH', icon: 'Ξ', color: '#627EEA' },
              ].map(c => {
                const rate = rates?.[c.sym];
                const price = rate?.usd || 0;
                const ch = rate?.change_24h || 0;
                const up = ch >= 0;
                return (
                  <div key={c.sym} className="flex items-center gap-2 px-1">
                    <span className="text-sm font-bold" style={{ color: c.color }}>{c.icon}</span>
                    <span className="text-[10px] font-bold text-foreground/80 flex-1">{c.sym}</span>
                    <span className="text-[10px] font-mono font-semibold text-foreground/70">${price >= 1000 ? (price/1000).toFixed(1) + 'k' : price.toFixed(2)}</span>
                    <span className={`text-[9px] font-bold ${up ? 'text-emerald-400' : 'text-red-400'}`}>{up ? '+' : ''}{ch.toFixed(1)}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {!isAdminRoute && <VerifBanner status={vStatus} to="/verification" onClick={onItemClick} />}
        <a
          href={TELEGRAM_SUPPORT_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onItemClick}
          data-testid="nav-support"
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium text-muted-foreground hover:text-sky-400 hover:bg-sky-500/[0.08] transition-all w-full group"
        >
          <MessageCircle size={17} strokeWidth={1.5} className="group-hover:scale-110 transition-transform" />
          <span className="flex-1 text-left">Поддержка</span>
          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-400 font-semibold border border-sky-500/20">TG</span>
        </a>
        <SidebarLink to="/profile" icon={Settings} label="Настройки" onClick={onItemClick} />
        <button onClick={() => { if (onItemClick) onItemClick(); logout(); }} data-testid="nav-logout"
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium text-muted-foreground hover:text-rose-400 hover:bg-rose-500/[0.06] transition-all w-full">
          <LogOut size={17} strokeWidth={1.5} />Выйти
        </button>
      </div>
    </>
  );

  return (
    <div className="flex overflow-hidden bg-background relative noise-overlay" style={{ touchAction: 'manipulation', height: '100dvh', height: '100vh', maxHeight: '-webkit-fill-available' }}>
      {/* Decorative animated background — skipped on the admin panel for max speed (static dark bg) */}
      {!isAdminRoute && (<>
      <div className="app-bg" />
      <AnimatedBackground />

      {/* Data Streams — Tron-style horizontal flows (hidden on mobile for readability) */}
      <div className="data-streams hidden md:block">
        {[...Array(5)].map((_, i) => (
          <div key={`stream-${i}`}>
            <div className="data-stream-line" style={{ top: `${18 + i * 16}%`, animationDuration: `${12 + i * 3}s`, animationDelay: `${i * 2.5}s` }} />
            <div className={`data-packet ${i % 3 === 1 ? 'magenta' : ''}`} style={{ top: `${18 + i * 16}%`, animationDuration: `${8 + i * 2}s`, animationDelay: `${i * 1.8 + 1}s` }} />
          </div>
        ))}
      </div>

      {/* Neon chain grid for cabinet */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute top-[28%] left-0 right-0 h-[1px]" style={{ background: 'linear-gradient(90deg, transparent 5%, rgba(0,240,255,0.03) 25%, rgba(0,240,255,0.07) 50%, rgba(0,240,255,0.03) 75%, transparent 95%)' }} />
        <div className="absolute top-[62%] left-0 right-0 h-[1px]" style={{ background: 'linear-gradient(90deg, transparent 10%, rgba(255,0,60,0.02) 35%, rgba(255,0,60,0.04) 55%, rgba(255,0,60,0.02) 80%, transparent)' }} />
        <div className="absolute top-0 bottom-0 left-[42%] w-[1px] hidden lg:block" style={{ background: 'linear-gradient(180deg, transparent 5%, rgba(0,240,255,0.03) 30%, transparent 50%, rgba(0,240,255,0.025) 70%, transparent 95%)' }} />
        <div className="absolute top-0 bottom-0 right-[15%] w-[1px] hidden lg:block" style={{ background: 'linear-gradient(180deg, transparent 15%, rgba(255,0,60,0.02) 40%, transparent 60%, rgba(255,0,60,0.015) 80%, transparent)' }} />
        <div className="absolute w-[4px] h-[4px] rounded-full neon-dot-pulse hidden lg:block" style={{ top: '28%', left: '42%', background: 'rgba(0,240,255,0.3)', color: 'rgba(0,240,255,0.35)' }} />
        <div className="absolute w-[3px] h-[3px] rounded-full neon-dot-pulse hidden lg:block" style={{ top: '62%', right: '15%', background: 'rgba(255,0,60,0.2)', color: 'rgba(255,0,60,0.25)', animationDelay: '1.5s' }} />
      </div>
      </>)}

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-64 z-10 relative sidebar-neon-border"
        style={{ background: 'hsl(220 30% 5%)', borderRight: '1px solid rgba(255,255,255,0.06)' }}>
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 md:hidden" onClick={() => setMobileOpen(false)} />
            <motion.aside initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }} transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className="fixed left-0 top-0 bottom-0 w-64 border-r border-border/30 z-50 flex flex-col md:hidden"
              style={{ background: 'hsl(220 30% 5%)', paddingTop: 'env(safe-area-inset-top)' }}>
              <SidebarContent onItemClick={() => setMobileOpen(false)} mobile />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0 relative z-10">
        <header className="border-b sticky top-0 z-20" style={{ background: 'rgba(8,12,22,0.85)', backdropFilter: 'blur(20px) saturate(1.3)', borderColor: 'rgba(255,255,255,0.06)', paddingTop: 'env(safe-area-inset-top)' }}>
          <div className="h-14 flex items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="md:hidden h-8 w-8" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"><Menu size={18} /></Button>
            {/* Status bar */}
            <div className="hidden md:flex items-center gap-2 px-3 py-1 rounded-lg" style={{ background: 'rgba(0,230,118,0.04)', border: '1px solid rgba(0,230,118,0.1)' }}>
              <div className="status-dot-live" />
              <span className="text-[10px] text-emerald-400/70 font-medium">Система активна</span>
              <span className="text-[9px] text-muted-foreground/40 font-mono">{new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative h-8 w-8 rounded-xl" data-testid="notifications-btn">
                  <Bell size={15} />
                  {unreadCount > 0 && <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-primary text-[9px] text-white rounded-full flex items-center justify-center font-bold">{unreadCount > 9 ? '9+' : unreadCount}</motion.span>}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 glass" data-testid="notifications-dropdown">
                <div className="flex items-center justify-between px-3 py-2.5 border-b border-border/30">
                  <span className="text-xs font-bold uppercase tracking-wider">Уведомления</span>
                  {unreadCount > 0 && <button onClick={markAllRead} className="text-[11px] text-primary hover:underline font-medium" data-testid="mark-all-read">Прочитать все</button>}
                </div>
                <ScrollArea className="max-h-[60vh] sm:max-h-[420px]">
                  {notifications.length === 0 ? <div className="p-4 text-center text-sm text-muted-foreground">Нет уведомлений</div> :
                    notifications.slice(0, 15).map(n => (
                      <div
                        key={n.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setExpandedNotif(n);
                          if (!n.read) markRead(n.id);
                        }}
                        className={`flex flex-col items-start gap-1 px-3 py-2.5 cursor-pointer hover:bg-secondary/40 transition-colors border-b border-border/20 last:border-b-0 ${!n.read ? 'bg-primary/5' : ''}`}
                        data-testid={`notification-${n.id}`}
                      >
                        <div className="flex items-center gap-2 w-full">
                          <NotifTypeIcon type={n.type} size={13} />
                          <span className="text-[13px] font-medium truncate flex-1">{n.title}</span>
                          <span className="text-[10px] text-muted-foreground whitespace-nowrap">{timeAgo(n.created_at)}</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground pl-5 line-clamp-2">{n.message}</p>
                      </div>
                    ))
                  }
                </ScrollArea>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 pl-2 h-8 rounded-xl" data-testid="user-menu-btn">
                  <div className="w-6 h-6 rounded-lg bg-primary/15 flex items-center justify-center text-primary text-[10px] font-bold">{user?.username?.[0]?.toUpperCase()}</div>
                  <span className="hidden sm:inline text-[13px] font-medium">{user?.username}</span>
                  <ChevronDown size={11} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44" data-testid="user-menu-dropdown">
                <div className="px-3 py-2"><p className="text-[13px] font-medium">{user?.username}</p><p className="text-[10px] text-muted-foreground">{user?.email}</p></div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { window.location.href = '/profile'; }} className="cursor-pointer" data-testid="profile-menu-btn"><User size={13} className="mr-2" />Профиль</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-destructive cursor-pointer"><LogOut size={13} className="mr-2" />Выйти</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto min-h-0 pb-28 md:pb-0" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
          <AnimatePresence mode="wait">
            <motion.div key={location.pathname} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} className="p-4 md:p-6 lg:p-8">
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
      {/* Mobile bottom navigation */}
      <MobileBottomNav />
      {/* Notification popup for unread notifications */}
      {!isAdmin && <NotificationPopup />}
      {/* Full-screen expanded notification reader — mobile uses native document scroll for reliability */}
      <AnimatePresence>
        {expandedNotif && (
          <NotificationReader
            notif={expandedNotif}
            onClose={() => setExpandedNotif(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Notification Reader (fullscreen on mobile, modal on desktop) ──
function NotificationReader({ notif, onClose }) {
  const scrollRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const scrollBy = (offset) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ top: offset, behavior: 'smooth' });
  };

  if (isMobile) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        ref={scrollRef}
        className="fixed inset-0 z-[80] bg-background"
        style={{
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
        }}
        data-testid="notif-reader-overlay"
      >
        {/* Sticky header */}
        <div
          className="sticky top-0 z-10 bg-background/95 backdrop-blur-md border-b border-border/40 px-5 py-3 flex items-start justify-between gap-3"
          data-testid="notif-reader-header"
        >
          <div className="flex items-start gap-2.5 min-w-0 flex-1">
            <div className="mt-0.5 flex-shrink-0"><NotifTypeIcon type={notif.type} size={20} /></div>
            <div className="min-w-0">
              <h3 className="text-[15px] font-bold leading-tight break-words">{notif.title}</h3>
              <p className="text-[11px] text-muted-foreground mt-1">{new Date(notif.created_at).toLocaleString('ru-RU', { dateStyle: 'medium', timeStyle: 'short' })}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground bg-secondary/40 hover:bg-secondary/80 active:bg-secondary transition-colors flex-shrink-0"
            data-testid="notif-reader-close"
            aria-label="Закрыть"
          >
            <X size={22} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5 pb-32" data-testid="notif-reader-body">
          <p className="text-[15px] leading-relaxed whitespace-pre-wrap break-words text-foreground/95">
            {notif.message}
          </p>
        </div>

        {/* Floating ↑↓ buttons for explicit scrolling */}
        <div className="fixed right-3 top-1/2 -translate-y-1/2 z-20 flex flex-col gap-2" data-testid="notif-reader-scroll-controls">
          <button
            onClick={() => scrollBy(-300)}
            className="w-12 h-12 rounded-full bg-primary/90 text-primary-foreground shadow-lg flex items-center justify-center active:scale-95 transition-transform"
            aria-label="Прокрутить вверх"
            data-testid="notif-reader-scroll-up"
            type="button"
          >
            <ChevronUp size={22} />
          </button>
          <button
            onClick={() => scrollBy(300)}
            className="w-12 h-12 rounded-full bg-primary/90 text-primary-foreground shadow-lg flex items-center justify-center active:scale-95 transition-transform"
            aria-label="Прокрутить вниз"
            data-testid="notif-reader-scroll-down"
            type="button"
          >
            <ChevronDown size={22} />
          </button>
        </div>

        {/* Sticky footer */}
        <div
          className="fixed left-0 right-0 bottom-0 z-10 bg-background/95 backdrop-blur-md border-t border-border/40 px-5 py-3 flex justify-end"
          style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
        >
          <button
            onClick={onClose}
            className="px-8 h-12 rounded-lg bg-primary text-primary-foreground font-semibold text-base hover:opacity-90 active:opacity-80"
            data-testid="notif-reader-close-bottom"
          >
            Закрыть
          </button>
        </div>
      </motion.div>
    );
  }

  // Desktop: classic modal
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/75"
      onClick={onClose}
      data-testid="notif-reader-overlay"
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 30, opacity: 0 }}
        className="relative bg-background border border-border/50 rounded-2xl shadow-2xl w-full max-w-lg flex flex-col"
        style={{ maxHeight: '85vh', minHeight: '480px' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="notif-reader-modal"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border/50">
          <div className="flex items-start gap-2.5 min-w-0 flex-1">
            <div className="mt-0.5"><NotifTypeIcon type={notif.type} size={18} /></div>
            <div className="min-w-0">
              <h3 className="text-base font-bold leading-tight break-words">{notif.title}</h3>
              <p className="text-[11px] text-muted-foreground mt-1">{new Date(notif.created_at).toLocaleString('ru-RU', { dateStyle: 'medium', timeStyle: 'short' })}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-secondary/80" data-testid="notif-reader-close" aria-label="Закрыть">
            <X size={20} className="text-muted-foreground" />
          </button>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4" data-testid="notif-reader-body">
          <p className="text-sm leading-relaxed whitespace-pre-wrap break-words text-foreground/95">{notif.message}</p>
        </div>
        <div className="px-5 py-3 border-t border-border/50 flex justify-end">
          <button onClick={onClose} className="px-6 h-11 rounded-lg bg-primary text-primary-foreground font-semibold" data-testid="notif-reader-close-bottom">
            Закрыть
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
